import React from "react";
import { Link } from "react-router-dom";
import { Avatar, Badge, InsightBox } from "../../../ForBrands/ui/BrandUI";
import {
  ListRule,
  MobileCard,
  MobileEmpty,
  MobileKpiTile,
  MobilePageHead,
  PayoutRow,
  StatList,
} from "../../../ForBrands/ui/mobile/MobileUI";

// Athlete Mode · Home on phones (Figma 01 · Home 2000:305; the scroll model
// of 2043:1904 is the shell's). Rendered by AthleteHomePage below 640px in
// the Athlete Mode shell. Presentational only: the page fetches, keeps the
// loading, error and upgrade states, and builds the display rows that both
// trees share. Figma leaves out the header buttons and the closing insight
// (both reachable from the Profile and Deals tabs).

const cx = (...parts) => parts.filter(Boolean).join(" ");

// Tone dot per attention kind (2019:1726).
const KIND_DOT = {
  offer: "bg-bm-orange",
  contract: "bg-[#2196f3]",
  deliverable: "bg-bm-attention",
  atlas: "bg-bm-tertiary",
};

const Arrow = () => (
  <span aria-hidden="true" className="ml-2">
    →
  </span>
);

// Overline section head that sits above its card (2019:1722).
const SectionHead = ({ children, right }) => (
  <div className="flex items-center gap-2">
    <h2 className="min-w-0 flex-1 bm-overline">{children}</h2>
    {right}
  </div>
);

// One attention item (2019:1726): the whole row is the link.
const AttentionItem = ({ item }) => {
  const body = (
    <>
      <span className="flex items-center gap-[10px]">
        <span aria-hidden="true" className={cx("h-2 w-2 shrink-0 rounded-full", KIND_DOT[item.kind] || "bg-bm-tertiary")} />
        <span className="min-w-0 flex-1 bm-h-xs text-bm-ink">{item.title}</span>
      </span>
      {item.meta && <span className="bm-body-s text-bm-muted">{item.meta}</span>}
      {item.href && item.action && (
        <span className="bm-label-l text-bm-orange">
          {item.action}
          <Arrow />
        </span>
      )}
    </>
  );
  const classes = "flex flex-col gap-2 break-words py-[14px]";
  return item.href ? (
    <Link to={item.href} className={classes}>
      {body}
    </Link>
  ) : (
    <div className={classes}>{body}</div>
  );
};

// One deal (2019:1807): a single action, so the whole card is the link.
// Campaign first, then the company.
const DealCard = ({ deal }) => {
  const cells = [
    { label: "Value", value: deal.value },
    { label: "Received", value: deal.received },
    { label: "Next payout", value: deal.nextPayout },
    { label: "Deliverables", value: deal.deliverables },
  ];
  return (
    <MobileCard as={Link} to={deal.action.href} className="break-words">
      <span className="flex items-center gap-3">
        <span aria-hidden="true" className="flex shrink-0">
          <Avatar initials={deal.initials} tone="muted" square size={40} className="!text-[12px] !font-semibold" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
          <span className="bm-h-xs text-bm-ink">{deal.campaign || deal.company}</span>
          {deal.campaign && deal.company && <span className="bm-body-s text-bm-muted">{deal.company}</span>}
        </span>
        <Badge tone={deal.statusTone} className="shrink-0">
          {deal.statusLabel}
        </Badge>
      </span>
      <ListRule />
      <span className="grid grid-cols-2 gap-x-3 gap-y-[10px]">
        {cells.map((c) => (
          <span key={c.label} className="flex min-w-0 flex-col gap-[2px]">
            <span className="bm-overline">{c.label}</span>
            <span className="bm-label-l text-bm-ink">{c.value}</span>
          </span>
        ))}
      </span>
      <span className="bm-label-l text-bm-orange">
        {deal.action.label}
        <Arrow />
      </span>
    </MobileCard>
  );
};

const AthleteHomeMobile = ({ title, subtitle, kpis, narrative, tiles, attention, payouts, deals }) => (
  <div className="flex flex-col gap-5">
    <MobilePageHead title={title} sub={subtitle} subSize="m" />

    {/* KPI grid (2019:1697): 2 x 2, the odd last tile full width. */}
    <div className="grid grid-cols-2 items-start gap-3">
      {kpis.map((k, i) => (
        <MobileKpiTile
          key={k.key}
          label={k.label}
          value={k.value}
          sub={k.sub}
          className={i === kpis.length - 1 && i % 2 === 0 ? "col-span-2" : undefined}
        />
      ))}
    </div>

    {/* Needs your attention (2019:1721) */}
    <div className="flex flex-col gap-[10px]">
      <SectionHead right={<span className="shrink-0 text-right bm-body-xs text-bm-muted">Sorted by urgency</span>}>
        Needs your attention
      </SectionHead>
      {attention.length ? (
        <MobileCard density="list">
          {attention.map((item, i) => (
            <React.Fragment key={`${item.kind}-${item.href || i}`}>
              {i > 0 && <ListRule />}
              <AttentionItem item={item} />
            </React.Fragment>
          ))}
        </MobileCard>
      ) : (
        <MobileEmpty>You're all caught up.</MobileEmpty>
      )}
    </div>

    {/* Deal activity (2019:1753) and its stats, moved out of the box (2019:1760). */}
    <InsightBox compact title="Your deal activity">
      {narrative}
    </InsightBox>
    <StatList rows={tiles} />

    {/* Upcoming payouts (2019:1778): Scheduled reads as text, the rest as a badge. */}
    <div className="flex flex-col gap-[10px]">
      <SectionHead>Upcoming payouts · direct to your bank</SectionHead>
      {payouts.length ? (
        <MobileCard density="list">
          {payouts.map((p, i) => (
            <React.Fragment key={p.key}>
              {i > 0 && <ListRule />}
              <PayoutRow
                label={p.label}
                sub={`${p.brand} · ${p.due}`}
                amount={p.amount}
                to={p.href}
                status={p.status === "scheduled" ? p.statusLabel : undefined}
                badge={p.status === "scheduled" ? undefined : p.statusLabel}
              />
            </React.Fragment>
          ))}
        </MobileCard>
      ) : (
        <MobileEmpty>Payouts from signed deals will show up here.</MobileEmpty>
      )}
    </div>

    {/* Deals (2019:1806), in place of the desktop table. */}
    {deals.length ? (
      // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
      <ul role="list" aria-label="Deals" className="flex flex-col gap-3">
        {deals.map((d) => (
          <li key={d.id}>
            <DealCard deal={d} />
          </li>
        ))}
      </ul>
    ) : (
      <MobileEmpty>Offers from brands will land here. Keep your profile complete so brands can find you in Discover.</MobileEmpty>
    )}
  </div>
);

export default AthleteHomeMobile;
